import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { AlertTriangle, X } from "lucide-react";

import { createAxiosInstance } from "api/axiosConfig";
import { FormField } from "components/form/FormField/FormField";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { DataLoadError } from "components/ui/DataLoadError";
import { useMedicationsById } from "features/medications/hooks/useMedicationById";
import { useMedicationsSearch } from "features/medications/hooks/useMedicationsSearch";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { useDrugAllergies } from "features/patients/hooks/useDrugAllergies";

import style from "./SelectDrug.module.css";

export const SelectDrug = ({ index, form, initialData, onAllergyCheck, isReadOnly = false }) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState(null);

  const patientId = useCurrentPatientId();
  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");
  const {
    data: drugAllergies,
    isError: isDrugAllergiesError,
    isFetching: isRefetchingDrugAllergies,
    refetch: refetchDrugAllergies,
  } = useDrugAllergies(patientId);

  const isManual = form.watch(`medicines.${index}.isManual`);

  const comboboxRef = useRef(null);
  const { data: initialMedicine } = useMedicationsById(initialData?.medicineId);

  const { data, isLoading } = useMedicationsSearch(search, isManual);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setIsOpen(true);
  };

  // Throws on failure on purpose: an empty list is "checked, none found", and
  // a failed check must never be reported as that (KIN-1615).
  const checkAllergyForDrug = async (drugId) => {
    const axiosInstance = await createAxiosInstance();
    const response = await axiosInstance.get(
      `${tenantVal}/drugsfile/checkallergy/${drugId}/patient/${patientId}`
    );

    return response.data || [];
  };

  const runAllergyCheck = async (drug) => {
    if (!onAllergyCheck) return;

    if (!drug?.id || !patientId || !tenantVal) {
      onAllergyCheck(index, drug?.id ?? null, [], { didFail: true, drug });
      return;
    }

    try {
      const allergies = await checkAllergyForDrug(drug.id);
      onAllergyCheck(index, drug.id, allergies, { didFail: false, drug });
    } catch (error) {
      console.error("Drug allergy check failed:", error);
      onAllergyCheck(index, drug.id, [], {
        didFail: true,
        drug,
        retry: () => runAllergyCheck(drug),
      });
    }
  };

  useEffect(() => {
    if (initialMedicine) {
      setSelectedDrug(initialMedicine);
      runAllergyCheck(initialMedicine);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMedicine]);

  const handleSelect = async (drug) => {
    setSelectedDrug(drug);
    form.setValue(`medicines.${index}.medicineId`, drug.id);
    form.setValue(`medicines.${index}.form`, drug.form);
    form.setValue(`medicines.${index}.dosage`, drug.strength);
    setIsOpen(false);

    await runAllergyCheck(drug);
  };

  const checkDrugAllergy = (drug) => {
    if (!drugAllergies || drugAllergies.length === 0) return null;

    return drugAllergies.find(
      (allergy) =>
        allergy.drugId === drug.id ||
        allergy.drugName?.toLowerCase() === drug.tradeName?.toLowerCase() ||
        allergy.drugGenericName?.toLowerCase() ===
          drug.genericName?.toLowerCase()
    );
  };

  const handleRemoveSelection = () => {
    setSelectedDrug(null);
    form.setValue(`medicines.${index}.medicineId`, "");
    form.setValue(`medicines.${index}.form`, "");
    form.setValue(`medicines.${index}.dosage`, "");
    setIsOpen(true);

    // Clear allergies for this drug
    if (onAllergyCheck) {
      onAllergyCheck(index, null, [], { didFail: false });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (comboboxRef.current && !comboboxRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [comboboxRef]);

  const renderDropdownContent = () => {
    if (search.length < 3) {
      return (
        <div className={style.dropdownItem}>
          Type at least 3 characters to search for medications
        </div>
      );
    }

    if (isLoading) {
      return <div className={style.dropdownItem}>Loading...</div>;
    }

    if (!data?.length) {
      return (
        <div className={style.dropdownItem}>
          <div className={style.noResults}>
            No medications found for "{search}"
          </div>
          <div className={style.noResultsHelper}>
            Try searching with a different name or term
          </div>
        </div>
      );
    }

    return data.map((drug) => {
      const allergy = checkDrugAllergy(drug);
      return (
        <div
          key={drug.id}
          className={`${style.dropdownItem} ${
            allergy ? style.allergyWarning : ""
          }`}
          onClick={() => handleSelect(drug)}
        >
          <div className={style.drugName}>
            {drug.tradeName}
            {allergy && (
              <span className={style.warningIcon} title="Drug Allergy Warning">
                ⚠️
              </span>
            )}
          </div>
          <div className={style.drugDetails}>
            <span>Pack Size: {drug.packSize}</span>
            {"•"}
            <span>Generic Name: {drug.genericName}</span>
            {"•"}
            <span>Manufacturer: {drug.manufacturer}</span>
            {"•"}
            <span>GMS No: {drug.gmsNo || "N/A"}</span>
          </div>
          {allergy && (
            <div className={style.allergyWarning}>
              <AlertTriangle
                size={13}
                style={{ flexShrink: 0, marginTop: 1 }}
              />
              <span>
                <strong>Drug allergy</strong> — reaction:{" "}
                {allergy.allergyReaction}
                {allergy.serious && (
                  <span className={style.seriousFlag}> • SERIOUS</span>
                )}
              </span>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div className={style.container} ref={comboboxRef}>
      {isDrugAllergiesError && (
        <DataLoadError
          label="this patient's recorded drug allergies"
          text="Medications below are not flagged against that list. This is not a statement that the patient has no drug allergies."
          onRetry={refetchDrugAllergies}
          isRetrying={isRefetchingDrugAllergies}
          className={style.allergyLoadError}
        />
      )}
      {isManual ? (
        <div className={style.manualInputs}>
          <FormField name={`medicines.${index}.tradeName`}>
            <label className={style.formLabel}>Trade Name</label>
            <input
              type="text"
              name="tradeName"
              className="form-control"
              {...form.register(`medicines.${index}.tradeName`)}
            />
          </FormField>
          <FormField name={`medicines.${index}.manufacturer`}>
            <div className="form-field-controll">
              <label className={style.formLabel}>Manufacturer</label>
              <input
                type="text"
                name="manufacturer"
                className="form-control"
                {...form.register(`medicines.${index}.manufacturer`)}
              />
            </div>
          </FormField>
          <FormField name={`medicines.${index}.packSize`}>
            <label className={style.formLabel}>Pack Size</label>
            <input
              type="text"
              name="packSize"
              className="form-control"
              {...form.register(`medicines.${index}.packSize`)}
            />
          </FormField>
        </div>
      ) : selectedDrug ? (
        <div
          className={`${style.selectedDrug} ${
            checkDrugAllergy(selectedDrug) ? style.selectedDrugAllergy : ""
          }`}
        >
          <div className={style.selectedDrugInfo}>
            <div className={style.drugName}>
              {selectedDrug.tradeName}
              {checkDrugAllergy(selectedDrug) && (
                <span
                  className={style.warningIcon}
                  title="Drug Allergy Warning"
                >
                  ⚠️
                </span>
              )}
            </div>
            <div className={style.drugDetails}>
              <span>Pack Size: {selectedDrug.packSize}</span>
              {"•"}
              <span>Generic Name: {selectedDrug.genericName}</span>
              {"•"}
              <span>Manufacturer: {selectedDrug.manufacturer}</span>
              {"•"}
              <span>GMS No: {selectedDrug.gmsNo || "N/A"}</span>
            </div>
            {checkDrugAllergy(selectedDrug) && (
              <div className={style.selectedAllergyWarning}>
                <AlertTriangle
                  size={15}
                  style={{ flexShrink: 0, marginTop: 2 }}
                />
                <span>
                  <strong>Drug allergy warning</strong> — patient is allergic to
                  this medication. <strong>Reaction:</strong>{" "}
                  {checkDrugAllergy(selectedDrug).allergyReaction}
                  {checkDrugAllergy(selectedDrug).serious && (
                    <span className={style.seriousFlag}> • SERIOUS</span>
                  )}
                </span>
              </div>
            )}
          </div>
          {!isReadOnly && (
            <button
              onClick={handleRemoveSelection}
              className={style.removeButton}
              type="button"
              aria-label="Remove selected drug"
            >
              <X size={16} />
            </button>
          )}
        </div>
      ) : (
        <div className={style.comboboxContainer}>
          <FormField name={`medicines.${index}.medicineId`}>
            <input
              type="text"
              className="form-control"
              placeholder="Search for medications..."
              onChange={handleInputChange}
              onFocus={() => setIsOpen(true)}
              value={search}
            />
          </FormField>
          {isOpen && (
            <div className={style.dropdown}>{renderDropdownContent()}</div>
          )}
        </div>
      )}
      <label className={style.manualToggle}>
        <CustomCheckbox
          checked={isManual}
          handleChange={(e) => {
            if (e.target.checked === false && selectedDrug) {
              form.setValue(`medicines.${index}.medicineId`, selectedDrug.id);
              form.setValue(`medicines.${index}.form`, selectedDrug.form);
              form.setValue(`medicines.${index}.dosage`, selectedDrug.strength);
              setIsOpen(false);
            }
            form.setValue(`medicines.${index}.isManual`, e.target.checked);
          }}
        />
        Manual Entry
      </label>
    </div>
  );
};
